import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useOrganization } from '../../contexts/OrganizationContext'
import { useSubscriptionFeatures } from '../../hooks/useSubscriptionFeatures'
import { useThemeColors } from '../../hooks/useThemeColors'
import { useBranding } from '../../hooks/useBranding'
import { useUserTypeSimple } from '../../hooks/useUserTypeSimple'
import type { UserRole } from '../../types'
import { ChevronDownIcon, ChevronRightIcon, ChevronLeftIcon, Bars3Icon } from '@heroicons/react/24/outline'
import { HelpCircle, Book, MessageCircle, Keyboard, ExternalLink } from 'lucide-react'
import { getFeatureConfig } from '../../config/featureTiers'
import { isDemoAccount } from '../../lib/demo'
import Modal from '../ui/Modal'

interface NavItem {
  label: string
  href: string
  badge?: number
  roles?: UserRole[]
  children?: NavItem[]
  tourId?: string
}

interface NavSection {
  title: string
  items: NavItem[]
}

interface SidebarProps {
  className?: string
  isCollapsed?: boolean
  onToggleCollapse?: () => void
}

// Inline HelpCenter component
function HelpCenter({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const { colors } = useThemeColors()

  const helpItems = [
    {
      icon: Book,
      title: 'Getting Started Guide',
      description: 'Learn the basics of setting up your facility',
      action: () => window.open('/docs/getting-started', '_blank')
    },
    {
      icon: MessageCircle,
      title: 'Chat with AI Assistant',
      description: 'Get instant help with any question',
      action: () => {
        onClose()
        // Trigger AI assistant - dispatch custom event
        window.dispatchEvent(new CustomEvent('openAIAssistant'))
      }
    },
    {
      icon: Keyboard,
      title: 'Keyboard Shortcuts',
      description: 'Speed up your workflow with shortcuts',
      shortcuts: [
        { key: '?', description: 'Open Help' },
        { key: '/', description: 'Search' },
        { key: 'Esc', description: 'Close dialogs' },
      ]
    },
    {
      icon: ExternalLink,
      title: 'Documentation',
      description: 'Browse full documentation',
      action: () => window.open('https://docs.aifacilitymanager.com', '_blank')
    }
  ]

  return (
    <Modal open={isOpen} onClose={onClose} title="Help Center" size="md">
      <Modal.Body>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {helpItems.map((item, index) => {
            const Icon = item.icon
            return (
              <button
                key={index}
                onClick={item.action}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                  padding: '16px',
                  backgroundColor: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  borderRadius: '12px',
                  cursor: item.action ? 'pointer' : 'default',
                  textAlign: 'left',
                  width: '100%',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (item.action) {
                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.06)'
                    e.currentTarget.style.borderColor = colors.primary
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.03)'
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.08)'
                }}
              >
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  backgroundColor: `${colors.primary}20`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <Icon size={20} style={{ color: colors.primary }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#f8fafc', fontWeight: 500, marginBottom: '4px' }}>
                    {item.title}
                  </div>
                  <div style={{ color: '#94a3b8', fontSize: '13px' }}>
                    {item.description}
                  </div>
                  {item.shortcuts && (
                    <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {item.shortcuts.map((shortcut, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <kbd style={{
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            color: '#f8fafc',
                            fontFamily: 'monospace'
                          }}>
                            {shortcut.key}
                          </kbd>
                          <span style={{ color: '#64748b', fontSize: '12px' }}>{shortcut.description}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </button>
            )
          })}
        </div>
      </Modal.Body>
    </Modal>
  )
}

export default function Sidebar({ className, isCollapsed = false, onToggleCollapse }: SidebarProps) {
  const location = useLocation()
  const { hasRole, isSuperAdmin, isOrganizationAdmin, isOrganizationStaff, hasOrganizationPermission, user } = useAuth()
  const { organization } = useOrganization()
  const { isTrainerMode, getTerminology, isLoading } = useSubscriptionFeatures()
  const { colors } = useThemeColors()
  const { branding } = useBranding()
  const { userType } = useUserTypeSimple()
  const isDemo = isDemoAccount(user?.email)
  const allSections = ['main', 'business', 'coaching', 'virtual assistant', 'settings', 'system']
  const [expandedItems, setExpandedItems] = useState<string[]>(
    isDemo ? allSections : ['main']
  )
  const [helpCenterOpen, setHelpCenterOpen] = useState(false)

  // Expand all sections when demo user is detected (handles async auth)
  useEffect(() => {
    if (isDemo) {
      setExpandedItems(allSections)
    }
  }, [isDemo])

  // Keyboard shortcut for Help (?)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if user is typing in an input or textarea
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        (e.target as HTMLElement).isContentEditable
      ) {
        return
      }

      if (e.key === '?' && !e.ctrlKey && !e.metaKey) {
        e.preventDefault()
        setHelpCenterOpen(true)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])
  
  // Determine nav item class based on portal type
  const getNavItemClass = () => {
    if (className?.includes('admin')) return 'admin-nav-item'
    if (className?.includes('staff')) return 'staff-nav-item'
    if (className?.includes('trainer')) return 'trainer-nav-item'
    return 'nav-item'
  }
  
  const navItemClass = getNavItemClass()

  // PHASE 1 FIX: Don't render admin sidebar on member routes
  if (location.pathname.startsWith('/member/') || location.pathname === '/member') {
    console.log('🔴 Admin Sidebar: Skipping render on member route')
    return null
  }
  
  const terminology = getTerminology()
  
  // Debug logging
  console.log('🟦 Admin Sidebar Rendering:', {
    pathname: location.pathname,
    isTrainerMode,
    isLoading,
    organizationName: organization?.name,
    tierType: organization?.subscription?.tier_type
  })

  // Super Admin Navigation
  const superAdminNavigation: NavSection[] = [
    {
      title: 'Platform',
      items: [
        { label: 'Dashboard', href: '/admin' },
        { label: 'Organizations', href: '/admin/organizations' },
        { label: 'Platform Users', href: '/admin/users' },
      ]
    },
    {
      title: 'System',
      items: [
        { label: 'Platform Settings', href: '/admin/settings' },
        { label: 'Feature Flags', href: '/admin/features' },
        { label: 'API Keys', href: '/admin/api-keys' },
        { label: 'Email Templates', href: '/admin/templates' },
      ]
    },
    {
      title: 'Analytics',
      items: [
        { label: 'Usage Reports', href: '/admin/analytics/usage' },
        { label: 'Revenue Reports', href: '/admin/analytics/revenue' },
        { label: 'System Health', href: '/admin/analytics/health' },
      ]
    },
    {
      title: 'Support',
      items: [
        { label: 'Audit Logs', href: '/admin/audit' },
        { label: 'Debug Console', href: '/admin/debug' },
      ]
    }
  ]

  // Get feature configuration
  const featureConfig = getFeatureConfig(userType, organization?.feature_tier)
  
  // Simplified Navigation for trainers/small gyms - NOW WITH ALL FEATURES FOR BETA
  const simplifiedNavigation: NavSection[] = [
    {
      title: 'Main',
      items: [
        { label: 'Dashboard', href: '/dashboard', tourId: 'sidebar-dashboard' },
        { label: 'Schedule', href: '/schedule', tourId: 'sidebar-schedule' },
        { label: 'Classes & Camps', href: '/classes-camps', roles: ['admin', 'staff'], tourId: 'sidebar-classes' },
        { label: 'Clients', href: '/clients', tourId: 'sidebar-members' },
        { label: 'Memberships', href: '/memberships', roles: ['admin', 'staff'] },
        { label: 'Packages', href: '/products' },
        { label: 'Teams', href: '/teams', roles: ['admin', 'staff'] },
        { label: 'Drill Library', href: '/practice-plans', roles: ['admin', 'staff'] },
        { label: 'Coach Studio', href: '/session-media', tourId: 'sidebar-coach-studio' },
        { label: 'Remote Sessions', href: '/remote-sessions', roles: ['admin', 'staff'] },
        { label: 'Contracts', href: '/contracts', roles: ['admin', 'staff'] },
        { label: 'Waivers', href: '/waivers' },
      ]
    },
    {
      title: 'Business',
      items: [
        { label: 'Finance', href: '/finance', roles: ['admin', 'staff'], tourId: 'sidebar-finance' },
        { label: 'Billing', href: '/billing' },
        { label: 'Email & SMS', href: '/marketing', roles: ['admin', 'staff'] },
        { label: 'Reports', href: '/analytics', roles: ['admin', 'staff'] },
      ]
    },
    {
      title: 'Virtual Assistant',
      items: [
        { label: 'Assistant Setup', href: '/assistant', roles: ['admin'], tourId: 'sidebar-assistant' },
        { label: 'Call History', href: '/assistant/calls', roles: ['admin', 'staff'] },
        { label: 'Actions', href: '/assistant/actions', roles: ['admin', 'staff'] },
        { label: 'SMS Inbox', href: '/assistant/sms', roles: ['admin', 'staff'] },
      ]
    },
    {
      title: 'Settings',
      items: [
        { label: 'General', href: '/settings', roles: ['admin'] },
        { label: 'Business', href: '/settings/business', roles: ['admin'] },
        { label: 'Booking Types', href: '/settings/categories', roles: ['admin'] },
        { label: 'Class & Rental Types', href: '/settings/class-types', roles: ['admin'] },
        { label: 'Membership Types', href: '/settings/membership-types', roles: ['admin'] },
        { label: 'Pricing Tiers', href: '/settings/pricing-tiers', roles: ['admin'] },
        { label: 'Email Templates', href: '/settings/templates', roles: ['admin'] },
        { label: 'Payments', href: '/settings/payments', roles: ['admin'] },
        { label: 'Staff & Instructors', href: '/settings/staff', roles: ['admin'] },
        { label: 'Team', href: '/team', roles: ['admin'] },
        // Hidden for demo - re-enable when needed
        // { label: 'Subscription', href: '/subscription', roles: ['admin'] },
      ]
    }
  ]

  // Facility Mode Navigation - Full featured
  const facilityNavigation: NavSection[] = [
    {
      title: 'Main',
      items: [
        { label: 'Dashboard', href: '/dashboard', tourId: 'sidebar-dashboard' },
        { label: 'Schedule', href: '/operations', roles: ['admin', 'staff'], tourId: 'sidebar-schedule' },
        { label: 'Classes & Camps', href: '/classes-camps', roles: ['admin', 'staff'], tourId: 'sidebar-classes' },
        { label: 'Members', href: '/members', roles: ['admin', 'staff'], tourId: 'sidebar-members' },
        { label: 'Memberships', href: '/memberships', roles: ['admin', 'staff'] },
        { label: 'Packages', href: '/products', roles: ['admin', 'staff'] },
        ...(!featureConfig.hideContracts ? [{ label: 'Contracts', href: '/contracts', roles: ['admin', 'staff'] }] : []),
        { label: 'Waivers', href: '/waivers', roles: ['admin', 'staff'] },
      ]
    },
    {
      title: 'Coaching',
      items: [
        { label: 'Teams', href: '/teams', roles: ['admin', 'staff'] },
        { label: 'Drill Library', href: '/practice-plans', roles: ['admin', 'staff'] },
        { label: 'Coach Studio', href: '/session-media', roles: ['admin', 'staff'], tourId: 'sidebar-coach-studio' },
        { label: 'Remote Sessions', href: '/remote-sessions', roles: ['admin', 'staff'] },
      ]
    },
    {
      title: 'Business',
      items: [
        { label: 'Finance', href: '/finance', roles: ['admin', 'staff'], tourId: 'sidebar-finance' },
        { label: 'Email & SMS', href: '/marketing', roles: ['admin', 'staff'] },
        ...(!featureConfig.hideAnalytics ? [{ label: 'Analytics', href: '/analytics', roles: ['admin', 'staff'] }] : []),
        ...(!featureConfig.hideTeam ? [{ label: 'Team', href: '/team', roles: ['admin'] }] : []),
        // Hidden for demo - re-enable when needed
        // { label: 'Subscription', href: '/subscription', roles: ['admin'] },
      ]
    },
    {
      title: 'Virtual Assistant',
      items: [
        { label: 'Assistant Setup', href: '/assistant', roles: ['admin'], tourId: 'sidebar-assistant' },
        { label: 'Call History', href: '/assistant/calls', roles: ['admin', 'staff'] },
        { label: 'Actions', href: '/assistant/actions', roles: ['admin', 'staff'] },
        { label: 'SMS Inbox', href: '/assistant/sms', roles: ['admin', 'staff'] },
      ]
    },
    {
      title: 'System',
      items: [
        { 
          label: 'Settings', 
          href: '/settings', 
          roles: ['admin'],
          children: [
            ...(!isTrainerMode ? [{ label: 'General', href: '/settings/general' }] : []),
            { label: 'Business', href: '/settings/business' },
            { label: 'Appearance', href: '/appearance' },
            ...(!isTrainerMode ? [{ label: 'Booking Types', href: '/settings/categories' }] : []),
            ...(!isTrainerMode ? [{ label: 'Class & Rental Types', href: '/settings/class-types' }] : []),
            ...(!isTrainerMode ? [{ label: 'Membership Types', href: '/settings/membership-types' }] : []),
            // Rental Rates moved to Finance page as a tab
            ...(!isTrainerMode ? [{ label: 'Pricing Tiers', href: '/settings/pricing-tiers' }] : []),
            ...(!isTrainerMode ? [{ label: 'Calendar', href: '/settings/calendar' }] : []),
            { label: 'Communication Settings', href: '/settings/templates' },
            ...(!isTrainerMode ? [{ label: terminology.Facilities, href: '/facilities' }] : []),
            { label: 'Integrations', href: '/settings/integrations' },
            { label: 'Notifications', href: '/settings/notifications' },
            { label: 'Payments', href: '/settings/payments' },
            { label: 'Staff & Instructors', href: '/settings/staff' },
            ...(!isTrainerMode ? [{ label: 'System', href: '/settings/system' }] : []),
          ].filter(Boolean)
        },
      ]
    }
  ]

  // Regular Navigation (Organization Admin/Staff)
  const organizationNavigation: NavSection[] =
    ((isTrainerMode || featureConfig.simplifiedSettings) ? simplifiedNavigation : facilityNavigation)

  const isActive = (href: string) => {
    if (href === '/') {
      return location.pathname === '/'
    }
    // Exact match OR nested route (path + /)
    return location.pathname === href || location.pathname.startsWith(href + '/')
  }

  const canAccessItem = (item: NavItem) => {
    if (!item.roles) return true
    return hasRole(item.roles)
  }

  const toggleExpanded = (label: string) => {
    setExpandedItems(prev => 
      prev.includes(label) 
        ? prev.filter(item => item !== label)
        : [...prev, label]
    )
  }

  const renderNavItem = (item: NavItem) => {
    if (!item || !item.label) {
      console.error('Invalid nav item:', item)
      return null
    }
    
    const hasChildren = item.children && item.children.length > 0
    const isExpanded = expandedItems.includes(item.label.toLowerCase())
    const isItemActive = isActive(item.href)
    const isChildActive = hasChildren && item.children.some(child => isActive(child.href))

    if (hasChildren) {
      return (
        <div key={item.href}>
          <button
            onClick={() => toggleExpanded(item.label.toLowerCase())}
            className={`${navItemClass} w-full text-left flex items-center`}
            style={{
              backgroundColor: isItemActive || isChildActive ? designColors.accentDim : 'transparent',
              color: isItemActive || isChildActive ? designColors.accent : designColors.textPrimary,
              borderLeft: isItemActive || isChildActive ? `2px solid ${designColors.accent}` : '2px solid transparent',
              paddingTop: '0.5rem',
              paddingRight: '0.75rem',
              paddingBottom: '0.5rem',
              paddingLeft: isItemActive || isChildActive ? 'calc(0.75rem - 2px)' : '0.75rem',
              fontSize: '0.875rem',
              fontWeight: isItemActive || isChildActive ? '500' : '400',
              fontFamily: "'Outfit', sans-serif",
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              borderRadius: '8px',
              margin: '2px 0',
              border: 'none',
              cursor: 'pointer',
              boxShadow: isItemActive || isChildActive ? `0 0 15px ${designColors.accentGlow}` : 'none'
            }}
            onMouseEnter={(e) => {
              if (!isItemActive && !isChildActive) {
                e.currentTarget.style.color = designColors.accent;
                e.currentTarget.style.backgroundColor = designColors.accentDim;
              }
            }}
            onMouseLeave={(e) => {
              if (!isItemActive && !isChildActive) {
                e.currentTarget.style.color = designColors.textPrimary;
                e.currentTarget.style.backgroundColor = 'transparent';
              }
            }}
          >
            {item.icon && <span className="nav-item-icon" style={{ fontSize: '1.125rem', marginRight: isCollapsed ? '0' : '0.5rem' }}>{item.icon}</span>}
            {!isCollapsed && (
              <>
                <span className="nav-item-text flex-1" style={{ letterSpacing: '0.02em', fontWeight: 'inherit' }}>{item.label}</span>
                <span className="nav-item-chevron" style={{
                  fontSize: '12px',
                  transition: 'transform 0.2s',
                  color: designColors.textSecondary
                }}>
                  {isExpanded ? '▲' : '▼'}
                </span>
              </>
            )}
          </button>
          {isExpanded && (
            <div className="ml-3" style={{ marginTop: '0.25rem' }}>
              {item.children.map((child) => (
                <Link
                  key={child.href}
                  to={child.href}
                  className={`${navItemClass} nav-item-child`}
                  style={{
                    backgroundColor: isActive(child.href) ? designColors.accentDim : 'transparent',
                    color: isActive(child.href) ? designColors.accent : designColors.textSecondary,
                    borderLeft: isActive(child.href) ? `2px solid ${designColors.accent}` : '2px solid transparent',
                    fontSize: '0.8125rem',
                    fontWeight: isActive(child.href) ? '500' : '400',
                    fontFamily: "'Outfit', sans-serif",
                    paddingTop: '0.375rem',
                    paddingBottom: '0.375rem',
                    paddingRight: '0.75rem',
                    paddingLeft: isActive(child.href) ? 'calc(1.75rem - 2px)' : '1.75rem',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    textDecoration: 'none',
                    display: 'block',
                    borderRadius: '6px',
                    margin: '2px 0',
                    boxShadow: isActive(child.href) ? `0 0 10px ${designColors.accentGlow}` : 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive(child.href)) {
                      e.currentTarget.style.color = designColors.accent;
                      e.currentTarget.style.backgroundColor = designColors.accentDim;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive(child.href)) {
                      e.currentTarget.style.color = designColors.textSecondary;
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  {child.icon && <span className="nav-item-icon text-sm" style={{ fontSize: '0.875rem', marginRight: '0.5rem' }}>{child.icon}</span>}
                  <span className="nav-item-text text-sm">{child.label}</span>
                </Link>
              ))}
            </div>
          )}
        </div>
      )
    }

    return (
      <Link
        key={item.href}
        to={item.href}
        className={navItemClass}
        title={isCollapsed ? item.label : ''}
        data-tour={item.tourId}
        style={{
          backgroundColor: isActive(item.href) ? designColors.accentDim : 'transparent',
          color: isActive(item.href) ? designColors.accent : designColors.textPrimary,
          borderLeft: isActive(item.href) ? `2px solid ${designColors.accent}` : '2px solid transparent',
          paddingTop: '0.5rem',
          paddingRight: '0.75rem',
          paddingBottom: '0.5rem',
          paddingLeft: isActive(item.href) ? 'calc(0.75rem - 2px)' : '0.75rem',
          fontSize: '0.875rem',
          fontWeight: isActive(item.href) ? '500' : '400',
          fontFamily: "'Outfit', sans-serif",
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          textDecoration: 'none',
          borderRadius: '8px',
          margin: '2px 0',
          display: 'flex',
          alignItems: 'center',
          boxShadow: isActive(item.href) ? `0 0 15px ${designColors.accentGlow}` : 'none'
        }}
        onMouseEnter={(e) => {
          if (!isActive(item.href)) {
            e.currentTarget.style.color = designColors.accent;
            e.currentTarget.style.backgroundColor = designColors.accentDim;
          }
        }}
        onMouseLeave={(e) => {
          if (!isActive(item.href)) {
            e.currentTarget.style.color = designColors.textPrimary;
            e.currentTarget.style.backgroundColor = 'transparent';
          }
        }}
      >
        {item.icon && <span className="nav-item-icon" style={{ fontSize: '1.125rem', marginRight: isCollapsed ? '0' : '0.5rem' }}>{item.icon}</span>}
        {!isCollapsed && (
          <span className="nav-item-text" style={{ letterSpacing: '0.02em', fontWeight: 'inherit' }}>{item.label}</span>
        )}
        {!isCollapsed && item.badge && (
          <span className="nav-item-badge" style={{
            backgroundColor: designColors.accent,
            color: designColors.bgDeep,
            padding: '2px 8px',
            borderRadius: '12px',
            fontSize: '0.75rem',
            fontWeight: '600',
            marginLeft: '8px'
          }}>{item.badge}</span>
        )}
      </Link>
    )
  }

  // Use appropriate navigation based on user role
  const navigationSections = isSuperAdmin ? superAdminNavigation : organizationNavigation

  // Design system colors - prefer branding.primary_color directly (most reliable)
  // Falls back to colors.accent from useThemeColors, then to cyan default
  const accentColor = branding?.primary_color || colors.accent || '#00f2ff'

  // Debug: log what color is being used
  console.log('🎨 Sidebar accent color:', {
    'branding?.primary_color': branding?.primary_color,
    'colors.accent': colors.accent,
    'final accentColor': accentColor,
    'branding object': branding ? 'loaded' : 'null'
  })
  const hexToRgbValues = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    if (!result) return '0, 242, 255'
    return `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
  }
  const accentRgb = hexToRgbValues(accentColor)

  const designColors = {
    bgDeep: '#080a0f',
    bgSidebar: 'rgba(8, 10, 15, 0.8)',
    accent: accentColor,
    accentDim: `rgba(${accentRgb}, 0.1)`,
    accentGlow: `rgba(${accentRgb}, 0.2)`,
    textPrimary: '#f8fafc',
    textSecondary: '#94a3b8',
    // Use the accent color for the border to match org branding
    borderDefault: `rgba(${accentRgb}, 0.3)`,
    borderHover: `rgba(${accentRgb}, 0.3)`,
  }

  return (
    <div className={`sidebar ${className || 'admin-sidebar'} ${isCollapsed ? 'collapsed' : ''}`} id="portal-sidebar" style={{
      backgroundColor: designColors.bgDeep,
      background: designColors.bgSidebar,
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      // Set borderRight directly using accentRgb to ensure org branding is applied
      // CSS variables weren't resolving correctly in rgba()
      borderRight: `2px solid rgba(${accentRgb}, 0.3)`,
      width: isCollapsed ? '60px' : '260px',
      minWidth: isCollapsed ? '60px' : '260px',
      boxShadow: '4px 0 24px rgba(0, 0, 0, 0.2)',
      transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
    }}>
      {/* Collapse Toggle Button */}
      <button
        onClick={onToggleCollapse}
        className="sidebar-toggle"
        style={{
          position: 'absolute',
          right: '10px',
          top: '20px',
          width: '28px',
          height: '28px',
          borderRadius: '8px',
          backgroundColor: designColors.bgDeep,
          border: `1px solid ${designColors.borderDefault}`,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
          color: designColors.accent,
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = designColors.borderHover;
          e.currentTarget.style.boxShadow = `0 0 15px ${designColors.accentGlow}`;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = designColors.borderDefault;
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.2)';
        }}
        title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {isCollapsed ? <ChevronRightIcon style={{ width: '16px', height: '16px' }} /> : <ChevronLeftIcon style={{ width: '16px', height: '16px' }} />}
      </button>

      {/* Logo Section - Use org branding logo if available */}
      <div className="logo-section" style={{
        padding: isCollapsed ? '0.75rem 0.5rem' : '1.5rem 1rem',
        paddingTop: '3.5rem',
        borderBottom: `1px solid ${designColors.borderDefault}`,
        marginBottom: '1.5rem',
        textAlign: 'center'
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <img
            src={branding?.logo_url || '/images/aifm-logo-cyan.png'}
            alt={branding?.company_name || organization?.name || 'AI Facility Manager'}
            style={{
              height: isCollapsed ? '40px' : '120px',
              width: 'auto',
              objectFit: 'contain',
              marginBottom: isCollapsed ? '0' : '0.75rem'
            }}
            onError={(e) => {
              // Fallback to text if logo not found
              e.currentTarget.style.display = 'none';
              const fallback = e.currentTarget.nextElementSibling as HTMLElement;
              if (fallback) fallback.style.display = 'block';
            }}
          />
          <div style={{ display: 'none', color: designColors.accent, fontSize: '1.25rem', fontWeight: '700', fontFamily: "'Space Grotesk', sans-serif" }}>
            {isCollapsed ? 'AIFM' : 'AI Facility Manager'}
          </div>
          {!isCollapsed && (
            <div className="logo-subtitle" style={{
              color: designColors.textSecondary,
              fontSize: '0.75rem',
              fontWeight: '400',
              fontFamily: "'Outfit', sans-serif"
            }}>
              {isSuperAdmin ? 'Platform Management' : (organization?.name || 'Loading...')}
            </div>
          )}
        </div>
      </div>

      {/* Navigation Sections */}
      {navigationSections.map((section) => {
        const visibleItems = section.items.filter(item => item && canAccessItem(item))
        
        if (visibleItems.length === 0) return null

        const isExpanded = expandedItems.includes(section.title.toLowerCase())
        
        return (
          <div key={section.title} className="nav-section" style={{
            marginBottom: '0.75rem',
            padding: '0'
          }}>
            {/* Collapsible Section Header */}
            {!isCollapsed && (
              <button
                onClick={() => toggleExpanded(section.title.toLowerCase())}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0.5rem 1rem',
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  borderRadius: '0',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  marginBottom: isExpanded ? '0.25rem' : '0',
                  borderBottom: `1px solid ${designColors.borderDefault}`
                }}
                onMouseEnter={(e) => {
                  const span = e.currentTarget.querySelector('span:first-child') as HTMLElement;
                  if (span) span.style.color = designColors.accent;
                }}
                onMouseLeave={(e) => {
                  const span = e.currentTarget.querySelector('span:first-child') as HTMLElement;
                  if (span) span.style.color = designColors.textSecondary;
                }}
              >
                <span style={{
                  color: designColors.textSecondary,
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  fontFamily: "'Outfit', sans-serif",
                  transition: 'color 0.2s ease'
                }}>{section.title}</span>
                <span style={{
                  fontSize: '10px',
                  color: designColors.textSecondary,
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease'
                }}>
                  ▼
                </span>
              </button>
            )}

            {/* Navigation Items */}
            {isExpanded && (
              <div style={{ padding: '0.25rem 0.75rem' }}>
                {visibleItems.map(renderNavItem)}
              </div>
            )}
          </div>
        )
      })}

      {/* Help Button - Fixed at bottom */}
      <div style={{
        marginTop: 'auto',
        padding: '0.75rem',
        borderTop: `1px solid ${designColors.borderDefault}`
      }}>
        <button
          onClick={() => setHelpCenterOpen(true)}
          className={navItemClass}
          title={isCollapsed ? 'Help (?)' : ''}
          style={{
            backgroundColor: 'transparent',
            color: designColors.textPrimary,
            border: 'none',
            paddingTop: '0.5rem',
            paddingRight: '0.75rem',
            paddingBottom: '0.5rem',
            paddingLeft: '0.75rem',
            fontSize: '0.875rem',
            fontWeight: '400',
            fontFamily: "'Outfit', sans-serif",
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            cursor: 'pointer'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = designColors.accent
            e.currentTarget.style.backgroundColor = designColors.accentDim
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = designColors.textPrimary
            e.currentTarget.style.backgroundColor = 'transparent'
          }}
        >
          <HelpCircle size={18} style={{ marginRight: isCollapsed ? '0' : '0.5rem', flexShrink: 0 }} />
          {!isCollapsed && (
            <>
              <span style={{ flex: 1, letterSpacing: '0.02em' }}>Help</span>
              <kbd style={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                padding: '2px 6px',
                borderRadius: '4px',
                fontSize: '11px',
                color: designColors.textSecondary,
                fontFamily: 'monospace'
              }}>?</kbd>
            </>
          )}
        </button>
      </div>

      {/* Help Center Modal */}
      <HelpCenter isOpen={helpCenterOpen} onClose={() => setHelpCenterOpen(false)} />
    </div>
  )
}
